import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuoteRequestsEnCours } from "../../lib/queries/quoteRequests";
import { CancelQuoteRequestDialog } from "../projects/_parts/CancelQuoteRequestDialog";

export function InProgressRequests() {
  const { t, i18n } = useTranslation();
  const enCours = useQuoteRequestsEnCours({ limit: 5 });
  const items = enCours.data?.items ?? [];

  /**
   * 9.BE.1 — la demande qu'on s'apprête à abandonner.
   *
   * On pouvait reprendre, jamais renoncer : les brouillons s'accumulaient sans
   * qu'aucun geste ne permette de dire « je n'en veux plus ». Le dialogue
   * existe depuis 9.L.8 avec sa raison obligatoire ; il était simplement
   * inatteignable d'ici, enfermé sur la page du dossier.
   */
  const [aAbandonner, setAAbandonner] = useState<{ id: string; titre: string | null } | null>(null);

  if (enCours.isLoading)
    return (
      <p role="status" className="mt-6">
        {t("app.loading")}
      </p>
    );
  if (enCours.error)
    return (
      <p role="alert" className="mt-6 text-red-700">
        {t("dashboard.error.partial")}
      </p>
    );
  if (items.length === 0)
    return (
      <p className="mt-6 rounded-xl border border-dashed border-slate-300 p-5 text-sm text-slate-600">
        {t("dashboard.kpi.in_progress_requests.empty")}
      </p>
    );

  return (
    <section
      aria-labelledby="demandes-en-cours"
      className="mt-6 rounded-xl border border-slate-200 bg-white p-5"
    >
      <h2 id="demandes-en-cours" className="text-base font-semibold text-slate-900">
        {t("requests.new.in_progress.title", { count: items.length })}
      </h2>
      <p className="mt-1 text-sm text-slate-600">{t("requests.new.in_progress.subtitle")}</p>

      <ul className="mt-3 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {items.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <span className="min-w-0 flex-1 basis-48">
              <span className="block truncate text-sm font-medium text-slate-900">
                {d.company_name ?? t("requests.new.in_progress.unknown_company")}
              </span>
              <span className="block truncate text-xs text-slate-500">
                {d.project_title ?? d.title ?? t("requests.new.in_progress.untitled")}
                {" · "}
                <span className="tabular-nums">
                  {t("requests.new.in_progress.started_on", {
                    date: new Date(d.created_at).toLocaleDateString(i18n.language),
                  })}
                </span>
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <Link
                to={`/quote-requests/${d.id}/preparer`}
                className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 inline-flex min-h-11 touch-manipulation items-center rounded-lg px-3 py-2 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {t("requests.new.in_progress.resume")}
              </Link>
              {/* Action secondaire, et discrète : abandonner est rare, et un
                  bouton aussi visible que « Reprendre » inviterait au clic
                  réflexe. La raison obligatoire du dialogue fait le reste. */}
              <button
                type="button"
                onClick={() =>
                  setAAbandonner({ id: d.id, titre: d.project_title ?? d.title ?? null })
                }
                className="focus-visible:ring-brand-500 min-h-11 touch-manipulation rounded-lg px-2 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2"
              >
                {t("requests.new.in_progress.abandon")}
              </button>
            </span>
          </li>
        ))}
      </ul>

      {enCours.data?.next_cursor && (
        <Link
          to="/projects"
          className="text-brand-700 mt-3 inline-flex min-h-11 items-center text-sm underline"
        >
          {t("workspace.more_drafts")}
        </Link>
      )}

      {aAbandonner && (
        <CancelQuoteRequestDialog
          quoteRequestId={aAbandonner.id}
          quoteRequestTitle={aAbandonner.titre}
          onClose={() => setAAbandonner(null)}
        />
      )}
    </section>
  );
}
